/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// node_modules
import _ from "lodash";
import React, { Component, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// application state
import ApplicationState from "applicationstate/ApplicationState.js";

// components - atoms
import FileInput from "components/atoms/FileInput/FileInput.js";

// components - layouts
import ErrorModal from "components/layouts/ErrorModal/ErrorModal.js";
import Notifications from "components/layouts/Notifications/Notifications.js";
import Page from "components/layouts/Page/Page.js";

// orchestration
import Firebase from "orchestration/firebase-library/Firebase.js";
import ROUTING from "orchestration/Routing.js";

// services
import ApplicationService from "orchestration/services/application-service/ApplicationService.js";

// styles
import "styles/bootstrap.css";
import "styles/kit/kit-animation.css";
import "styles/kit/kit-background.css";
import "styles/kit/kit-border.css";
import "styles/kit/kit-gradient.css";
import "styles/kit/kit-text.css";
import "styles/kit/kit-user.css";
import "styles/kit/kit-zindex.css";

// utils
import InteractionUtils from "utils/interaction-utils/InteractionUtils.js";
import LoggerUtils from "utils/logger-utils/LoggerUtils.js";
import RedirectUtils from "utils/redirect-utils/RedirectUtils.js";

// pages (lazy loads must be after import statements)
import ConnectionErrorPage from "components/pages/ConnectionErrorPage/ConnectionErrorPage.js";
const BootPage = lazy(() => import("components/pages/BootPage/BootPage.js"));
const CollectionsGalleryPage = lazy(() => import("components/pages/CollectionsGalleryPage/CollectionsGalleryPage.js"));
const CollectionsPage = lazy(() => import("components/pages/CollectionsPage/CollectionsPage.js"));
const DeveloperPage = lazy(() => import("components/pages/DeveloperPage/DeveloperPage.js"));
const EditorPage = lazy(() => import("components/pages/EditorPage/EditorRoutes/EditorRoutes.js"));
const FALLBACK_PAGE = <Page isLoading={true}/>;
const InformationPage = lazy(() => import("components/pages/InformationPage/InformationRoutes/InformationRoutes.js"));
const ItemGalleryPage = lazy(() => import("components/pages/ItemGalleryPage/ItemGalleryPage.js"));
const ItemPage = lazy(() => import("components/pages/ItemPage/ItemPage.js"));
const LoginPage = lazy(() => import("components/pages/LoginPage/LoginPage.js"));
const MapPage = lazy(() => import("components/pages/MapPage/MapPage.js"));
const NotFoundPage = lazy(() => import("components/pages/404Page/404Page.js"));
const QrCodePage = lazy(() => import("components/pages/QrCodePage/QrCodeRoutes/QrCodeRoutes.js"));

// constants
const APPLICATION_DISPLAY_WIDTH_MINIMUM = Number("300");
const APPLICATION_VERSION = String("050");
const BREAKPOINT_XSMALL = Number("375");
const BREAKPOINT_SMALL = Number("576");
const BREAKPOINT_MEDIUM = Number("768");
const BREAKPOINT_LARGE = Number("992");
const BREAKPOINT_XLARGE = Number("1200");
const BREAKPOINT_XXLARGE = Number("1400");
const FOOTER_SIZE_DESKTOP = Number("100");
const FOOTER_SIZE_MOBILE = Number("75");
const HEADER_SIZE_DESKTOP = Number("100");
const HEADER_SIZE_MOBILE = Number("75");
const RESIZE_MINIMUM_WAIT_TIME = Number("50");

class App extends Component {
  constructor(props) {
    super(props);

    // set applicaiton environment default to production
    const { production } = ApplicationState.getApplicationEnvironments();

    // application state
    this.state = {
      applicationEnvironment: String(production),
      application: null,
      applicationReady: Boolean(false),
      applicationVersion: String(APPLICATION_VERSION),
      breakpoint: String("xx_small"),
      error: String(""),
      fallback: FALLBACK_PAGE,
      footerHeight: Number("75"),
      headerHeight: Number("75"),
      increment: Number("1"),
      showConnectionError: Boolean(false),
      showDisplaySizeWarning: Boolean(false),
    };

    // debounce resize handler
    this.handleResize = _.debounce(
      this.handleResize.bind(this),
      RESIZE_MINIMUM_WAIT_TIME,
      { trailing: true }
    );
  }

  render() {
    const { state } = this;
    const {
      applicationReady,
      error,
      fallback,
      redirect,
      redirectUrl,
      showConnectionError,
    } = state;

    if(applicationReady) {
      return(
        <React.Fragment>
          <Router>
            <Suspense fallback={fallback}>
              <Routes>

                {/* 404 - No Path */}
                <Route
                  path={ROUTING.notFound}
                  element={<NotFoundPage/>}
                />

                {/* Boot Page */}
                <Route
                  exact
                  path={ROUTING.boot}
                  element={<BootPage/>}
                />

                {/* Collections Gallery Page */}
                <Route
                  exact
                  path={ROUTING.collections}
                  element={<CollectionsGalleryPage/>}
                />

                {/* Collections Page */}
                <Route
                  exact
                  path={`${ROUTING.collections}/:id`}
                  element={<CollectionsPage/>}
                />

                {/* Developer Page & Login Page */}
                <Route
                  exact
                  path={`${ROUTING.dev.root}/*`}
                  element={Firebase.user
                    ? <DeveloperPage/>
                    : <LoginPage/>
                  }
                />

                {/* Editor Page & Login Page */}
                <Route
                  exact
                  path={`${ROUTING.edit.root}/*`}
                  element={Firebase.user
                    ? <EditorPage/>
                    : <LoginPage/>
                  }
                />

                {/* Info Page */}
                <Route
                  exact
                  path={`${ROUTING.info.root}/*`}
                  element={<InformationPage/>}
                />

                {/* Item Gallery Page */}
                <Route
                  exact
                  path={ROUTING.gallery}
                  element={<ItemGalleryPage/>}
                />

                {/* Items Page */}
                <Route
                  exact
                  path={`${ROUTING.items}/:id`}
                  element={<ItemPage/>}
                />

                {/* Map Page */}
                <Route
                  exact
                  path={ROUTING.map}
                  element={<MapPage/>}
                />

                {/* QR Page */}
                <Route
                  exact
                  path={`${ROUTING.qr.root}/*`}
                  element={<QrCodePage/>}
                />

              </Routes>
            </Suspense>

            {/* Redirect */}
            {redirect && (<Navigate to={redirectUrl}/>)}
          </Router>

          {/* Error Modal */}
          {error && (
            <ErrorModal
              error={error}
              onClose={() => this.setState({ error: Boolean(false) })}
            />
          )}

          {/* File Uploader */}
          <FileInput/>

          {/* Notifications */}
          <Notifications/>

        </React.Fragment>
      );
    }
    else if(showConnectionError) {
      return (<ConnectionErrorPage/>);
    }
    else {
      return (fallback);
    }
  }

  componentDidMount() {
    this.handleInitializeApplication();
  }

  componentDidUpdate() {
    this.handleRedirect();
  }

  getApplicationDomain = () => {
    const origin = String(window.location.origin).toLowerCase();
    const domain = origin
      .replace("https://", "")
      .replace("http://", "")
      .replace("www.", "")
      .replace(":3000", "")
      .split(".")[0];
    const isDevelopmentDomain = (
      domain === "dev" ||
      domain === "localhost" ||
      domain === "192"
    );
    return ({
      domain: String(`${isDevelopmentDomain ? "dev" : domain}-${APPLICATION_VERSION}`),
      isDevelopmentDomain
    });
  };

  handleInitializeApplication = () => {
    const { development, production } = ApplicationState.getApplicationEnvironments();

    // 1) application state
    ApplicationState.initialize(
      () => this.state,
      (newState, callback) => this.setState(newState, callback)
    );

    // 2) application domain
    const { isDevelopmentDomain: isDevEnv, domain } = this.getApplicationDomain();
    const applicationEnvironment = isDevEnv ? development : production;
    this.setState({ applicationEnvironment: String(applicationEnvironment) });

    // 3) logger-utils
    LoggerUtils.initialize(applicationEnvironment);

    // 4) redirect-utils & redirect subscriber
    RedirectUtils.subscribe((redirectUrl) => this.setState({ redirectUrl }));
    this.handleResize();

    // 5) interaction-utils & resize subscriber
    InteractionUtils.subscribe("resize", () => this.handleResize());

    // 6) firebase initialization
    Firebase.initialize(domain)
    .then((appVersion) => {
      if(String(appVersion) !== String(APPLICATION_VERSION)) {
        window.location.reload(true);
      }
    })

    // 7) application information sync
    .then(() => {
      ApplicationService.get()
      .then((application) => {
        this.setState({
          applicationEnvironment: isDevEnv
            ? development
            : production,
          applicationReady: true,
          application,
        });
      });
    })
    .catch((error) => {
      this.setState({ showConnectionError: true });
      LoggerUtils.error(error);
    });
  };

  handleRedirect = () => {
    const { state } = this;
    const { lastRedirectUrl, redirectUrl } = state;
    if(redirectUrl) {
      if(redirectUrl === lastRedirectUrl) {
        this.setState({
          lastRedirectUrl: Boolean(false),
          redirect: Boolean(false),
          redirectUrl: Boolean(false),
        });
      }
      else {
        this.setState({
          lastRedirectUrl: redirectUrl,
          redirect: true
        });
      }
    }
  };

  handleResize = () => {
    const { state } = this;
    const { breakpoint, showDisplaySizeWarning } = state;
    const { innerWidth, outerWidth } = window;

    // check application window size
    const minSize = Number(Math.min(innerWidth, outerWidth));
    let newSize = String("xx_small");
    if (minSize > BREAKPOINT_XSMALL) newSize = String("x_small");
    if (minSize >= BREAKPOINT_SMALL) newSize = String("small");
    if (minSize >= BREAKPOINT_MEDIUM) newSize = String("medium");
    if (minSize >= BREAKPOINT_LARGE) newSize = String("large");
    if (minSize >= BREAKPOINT_XLARGE) newSize = String("x_large");
    if (minSize >= BREAKPOINT_XXLARGE) newSize = String("xx_large");

    // update state
    if (breakpoint !== newSize) {
      const mobileSizing = newSize.includes("small");
      const footerHeight = mobileSizing
        ? Number(FOOTER_SIZE_MOBILE)
        : Number(FOOTER_SIZE_DESKTOP);
      const headerHeight = mobileSizing
        ? Number(HEADER_SIZE_MOBILE)
        : Number(HEADER_SIZE_DESKTOP);

      this.setState({
        breakpoint: newSize,
        footerHeight,
        headerHeight,
        mobileSizing
      });
      LoggerUtils.info(`Size: ${newSize}`);
    }

    // warn user if application window too small
    if(!showDisplaySizeWarning && minSize < APPLICATION_DISPLAY_WIDTH_MINIMUM) {
      this.setState({ showDisplaySizeWarning: true });
    }
    else if(showDisplaySizeWarning) {
      this.setState({ showDisplaySizeWarning: false });
    }
  };
}
export default App;
